import type { FC } from 'react';
import { Page } from '@/components/page';
import { Button } from '@ziggiz/ui';
import { LockKeyholeIcon, LoaderCircleIcon } from 'lucide-react';
import { AuthBrandPanel } from './auth-brand-panel';
import { AuthBackground } from './auth-background';
import type { AuthSnapshot } from './auth-client';

interface AuthScreenProps {
  status: AuthSnapshot['status'];
  message?: string;
  onSignIn?: () => void;
  onReload?: () => void;
}

export const AuthScreen: FC<AuthScreenProps> = ({
  status,
  message,
  onSignIn,
  onReload
}) => {
  const busy = status === 'loading' || status === 'redirecting';
  const configured = status !== 'configuration-error';
  const needsRecovery =
    status === 'error' ||
    status === 'configuration-error' ||
    status === 'interaction-required';
  const title = busy
    ? 'Connecting to Microsoft'
    : configured
      ? 'Sign in to Ziggiz'
      : 'Sign-in unavailable';

  return (
    <Page
      title={title}
      className="relative isolate flex min-h-dvh max-w-none flex-col overflow-hidden bg-background px-6 py-6 sm:px-10 sm:py-8 lg:px-16"
    >
      <AuthBackground />
      <header className="relative z-1 mx-auto w-full max-w-6xl">
        <img
          src="/ziggiz-logo.svg"
          alt="Ziggiz"
          width={1288}
          height={400}
          className="h-auto w-36"
        />
      </header>
      <div className="relative z-1 mx-auto grid w-full max-w-6xl flex-1 items-center gap-12 py-16 lg:grid-cols-[1.1fr_1fr] lg:gap-20 lg:py-10">
        <div className="hidden lg:block">
          <AuthBrandPanel />
        </div>
        <section
          aria-labelledby="auth-heading"
          className="mx-auto w-full max-w-sm lg:mx-0"
        >
          <h1
            id="auth-heading"
            className="text-headline font-medium tracking-tight text-balance text-foreground"
          >
            {title}
          </h1>
          <p
            role={busy ? 'status' : status === 'signed-out' ? undefined : 'alert'}
            className="mt-4 text-body-lg leading-relaxed text-[var(--z-text-secondary)]"
          >
            {message ??
              (busy
                ? 'Preparing your session…'
                : 'Use your organization’s Microsoft account to continue.')}
          </p>
          {!busy ? (
            <div className="mt-9 flex flex-col items-center gap-4">
              {configured ? (
                <Button
                  className="h-13 w-full gap-3 [font-size:var(--text-body-lg)] [line-height:var(--text-body-lg--line-height)] shadow-none"
                  onClick={onSignIn}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-4"
                  >
                    <path d="M0 0h9v9H0zM11 0h9v9h-9zM0 11h9v9H0zM11 11h9v9h-9z" />
                  </svg>
                  Sign in with Microsoft
                </Button>
              ) : null}
              {needsRecovery && onReload ? (
                <Button
                  variant={configured ? 'ghost' : 'outline'}
                  className={
                    configured ? 'min-h-11 text-[var(--z-text-secondary)]' : 'h-13 w-full'
                  }
                  onClick={onReload}
                >
                  Reload page
                </Button>
              ) : null}
            </div>
          ) : (
            <LoaderCircleIcon
              aria-hidden="true"
              className="mt-9 size-6 animate-spin text-[var(--z-text-secondary)] motion-reduce:animate-none"
            />
          )}
          <div className="mt-8 flex items-start gap-2.5 border-t border-[var(--z-border-subtle)] pt-6 text-body-sm leading-relaxed text-[var(--z-text-secondary)]">
            <LockKeyholeIcon
              aria-hidden="true"
              className="mt-0.5 size-3.5 shrink-0"
              strokeWidth={1.6}
            />
            <p>Your organization manages your access.</p>
          </div>
        </section>
      </div>
      <footer className="relative z-1 mx-auto flex w-full max-w-6xl items-center justify-center gap-6 text-body-sm text-[var(--z-text-secondary)]">
        <a
          href="https://www.ziggiz.ai/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center rounded-sm underline-offset-[0.25em] hover:text-[var(--z-text-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--z-focus-ring)]"
        >
          Terms of Service<span className="sr-only"> (opens in a new tab)</span>
        </a>
        <a
          href="https://www.ziggiz.ai/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center rounded-sm underline-offset-[0.25em] hover:text-[var(--z-text-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--z-focus-ring)]"
        >
          Privacy Policy<span className="sr-only"> (opens in a new tab)</span>
        </a>
      </footer>
    </Page>
  );
};
